import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { InventoryReportsTitleBarState } from "../types";

// Initial state
const initialState: InventoryReportsTitleBarState = {
  companies: [],
  items: [],
  warehouseLocations: [],
  isLoading: true,
  assemblyItems: [],
  bomData: [],
  error: "",
  isError: false,
};

// Async thunk for fetching all options
export const fetchOptions = createAsyncThunk(
  "inventoryReportsTitleBar/fetchOptions",
  async (_, { extra }: any) => {
    if (!extra?.apiClient) {
      throw new Error("API client not configured for inventory reports title bar slice");
    }
    
    const { apiClient } = extra;
    
    // Fetch all data in parallel
    const [i, c, wl, ai] = await Promise.all([
      handleFetchItem(apiClient),
      handleFetchCompanies(apiClient),
      handleFetchWarehouseLocations(apiClient),
      fetchAssemblyItems(apiClient),
    ]);
    
    return { i, c, wl, ai };
  }
);

export const fetchBom = createAsyncThunk(
  "inventoryReportsTitleBar/fetchBom",
  async (_, { extra }: any) => {
    if (!extra?.apiClient?.getV1BillsOfMaterials) {
      throw new Error("API client not configured for BOM fetch");
    }
    const bom = await fetchBomData(extra.apiClient);
    return { bom };
  }
);

// Helper functions
const handleFetchCompanies = async (apiClient: any) => {
  try {
    if (!apiClient.getV1Company) {
      throw new Error("getV1Company not available in API client");
    }
    const response = await apiClient.getV1Company();
    return response?.data?.company || [];
  } catch (error) {
    console.error("Error fetching companies:", error);
    return [];
  }
};

const handleFetchItem = async (apiClient: any) => {
  try {
    if (!apiClient.getV1InventoryItems) {
      throw new Error("getV1InventoryItems not available in API client");
    }
    const response = await apiClient.getV1InventoryItems({
      skip: 0,
      limit: 999,
      filters: `(type.eq=inventory|type.eq=assembly&is_variant.eq=false|self_variant.eq=true)`,
    });
    return response?.data?.inventoryItemsList || [];
  } catch (error) {
    console.error("Error fetching items:", error);
    return [];
  }
};

const handleFetchWarehouseLocations = async (apiClient: any) => {
  try {
    if (!apiClient.getV1WarehouseLocation) {
      throw new Error("getV1WarehouseLocation not available in API client");
    }
    const response = await apiClient.getV1WarehouseLocation({
      skip: 0,
      limit: 999,
    });
    return response?.data?.warehouseLocationList || [];
  } catch (error) {
    console.error("Error fetching warehouse locations:", error);
    return [];
  }
};

const fetchAssemblyItems = async (apiClient: any) => {
  try {
    if (!apiClient.getV1AssemblyItems) {
      throw new Error("getV1AssemblyItems not available in API client");
    }
    const response = await apiClient.getV1AssemblyItems({
      skip: 0,
      limit: 999,
    });
    return response?.data?.assemblyItemsList || [];
  } catch (error) {
    console.error("Error fetching assembly items:", error);
    return [];
  }
};

const fetchBomData = async (apiClient: any) => {
  try {
    const response = await apiClient.getV1BillsOfMaterials({
      skip: 0,
      limit: 99999,
    });
    return response?.data?.bom_list || [];
  } catch (error) {
    console.error("Error fetching BOM data:", error);
    return [];
  }
};

export const inventoryReportsTitleBarSlice = createSlice({
  name: "inventoryReportsTitleBar",
  initialState,
  reducers: {
    setCompanies: (state, action) => {
      state.companies = action.payload;
    },
    setItems: (state, action) => {
      state.items = action.payload;
    },
    setWarehouseLocations: (state, action) => {
      state.warehouseLocations = action.payload;
    },
    setAssemblyItems: (state, action) => {
      state.assemblyItems = action.payload;
    },
    setBomData: (state, action) => {
      state.bomData = action.payload;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.isError = !!action.payload;
    },
    resetInventoryReportsTitleBarState: (state) => {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    /*-------------------------------- fetch options Pending State ---------------------------------*/
    builder.addCase(fetchOptions.pending, (state) => {
      state.companies = [];
      state.items = [];
      state.warehouseLocations = [];
      state.assemblyItems = [];
      state.isLoading = true;
      state.error = "";
      state.isError = false;
    });
    
    builder.addCase(fetchOptions.fulfilled, (state, action) => {
      state.companies = action.payload?.c || [];
      state.items = action.payload?.i || [];
      state.warehouseLocations = action.payload?.wl || [];
      state.assemblyItems = action.payload?.ai || [];
      state.isLoading = false;
      state.isError = false;
      state.error = "";
    });
    
    builder.addCase(fetchOptions.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to fetch options";
      state.isError = true;
    });
    
    builder.addCase(fetchBom.fulfilled, (state, action) => {
      state.bomData = action.payload?.bom || [];
    });
    
    builder.addCase(fetchBom.rejected, (state, action) => {
      state.error = action.error.message || "Failed to fetch BOM data";
      state.isError = true;
    });
  },
});

export const { 
  setCompanies, 
  setItems, 
  setWarehouseLocations, 
  setAssemblyItems, 
  setBomData, 
  setLoading, 
  setError, 
  resetInventoryReportsTitleBarState 
} = inventoryReportsTitleBarSlice.actions;

export default inventoryReportsTitleBarSlice.reducer;